import { useState, type FormEvent, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Logo } from '@/components/Logo';

/* ------------------------------------------------------------------ */
/*  Error messages                                                     */
/* ------------------------------------------------------------------ */

function getErrorMessage(code: string, t: TFunction): string {
  switch (code) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return t('errors.invalidCredential');
    case 'auth/email-already-in-use':
      return t('errors.emailInUse');
    case 'auth/weak-password':
      return t('errors.weakPassword');
    case 'auth/too-many-requests':
      return t('errors.tooManyRequests');
    default:
      return t('errors.unknown');
  }
}

/* ------------------------------------------------------------------ */
/*  3D Scene for left panel — cream wireframe                          */
/* ------------------------------------------------------------------ */

function RotatingWireframe() {
  const meshRef = useRef<THREE.Mesh>(null);
  const edgesRef = useRef<THREE.LineSegments>(null);

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (meshRef.current) {
      meshRef.current.rotation.x = t * 0.1;
      meshRef.current.rotation.y = t * 0.15;
    }
    if (edgesRef.current) {
      edgesRef.current.rotation.x = t * 0.1;
      edgesRef.current.rotation.y = t * 0.15;
    }
  });

  const geometry = new THREE.IcosahedronGeometry(2.2, 1);
  const edges = new THREE.EdgesGeometry(geometry);

  return (
    <group>
      <lineSegments ref={edgesRef} geometry={edges}>
        <lineBasicMaterial color="#EFE3CA" transparent opacity={0.5} />
      </lineSegments>
      <mesh ref={meshRef} geometry={geometry}>
        <meshBasicMaterial color="#56B6C6" transparent opacity={0.06} />
      </mesh>
    </group>
  );
}

function FloatingParticles() {
  const pointsRef = useRef<THREE.Points>(null);
  const count = 60;
  const positions = useRef(new Float32Array(count * 3));

  useEffect(() => {
    for (let i = 0; i < count; i++) {
      positions.current[i * 3] = (Math.random() - 0.5) * 10;
      positions.current[i * 3 + 1] = (Math.random() - 0.5) * 10;
      positions.current[i * 3 + 2] = (Math.random() - 0.5) * 10;
    }
  }, []);

  useFrame((state) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y = state.clock.getElapsedTime() * 0.02;
      pointsRef.current.rotation.x = state.clock.getElapsedTime() * 0.01;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions.current, 3]}
          count={count}
        />
      </bufferGeometry>
      <pointsMaterial color="#56B6C6" size={0.04} transparent opacity={0.55} sizeAttenuation />
    </points>
  );
}

function AuthScene() {
  return (
    <Canvas
      camera={{ position: [0, 0, 6], fov: 45 }}
      className="pointer-events-none absolute inset-0"
      style={{ background: 'transparent' }}
      gl={{ alpha: true, antialias: true }}
    >
      <ambientLight intensity={0.4} />
      <RotatingWireframe />
      <FloatingParticles />
    </Canvas>
  );
}

/* ------------------------------------------------------------------ */
/*  Auth Page                                                          */
/* ------------------------------------------------------------------ */

export function AuthPage() {
  const { t } = useTranslation('auth');
  const { signInWithEmail, signUpWithEmail } = useAuth();
  const [isSignup, setIsSignup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      if (isSignup) {
        await signUpWithEmail(email, password);
      } else {
        await signInWithEmail(email, password);
      }
    } catch (err: unknown) {
      const firebaseError = err as { code?: string };
      setError(getErrorMessage(firebaseError.code ?? '', t));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col bg-k-canvas lg:flex-row">
      {/* ── Left panel — indigo brand panel (desktop only) ── */}
      <div className="relative hidden overflow-hidden bg-brand-indigo lg:flex lg:w-[55%] lg:flex-col">
        {/* Flat color blocks */}
        <div className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-brand-indigo-hover" />
        <div className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-brand-teal/30" />

        {/* Dot pattern in cream */}
        <div
          className="pointer-events-none absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'radial-gradient(circle, #EFE3CA 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />

        <AuthScene />

        {/* Logo */}
        <Link to="/" className="relative z-10 flex items-center gap-2.5 p-8">
          <Logo className="h-10 w-10 text-brand-cream" />
          <span className="font-display text-xl font-bold tracking-tight text-brand-cream">
            AgentCAD
          </span>
        </Link>

        {/* Tagline at bottom */}
        <div className="relative z-10 mt-auto p-10">
          <p className="font-display text-3xl font-extrabold leading-tight text-brand-cream xl:text-4xl">
            {t('heroTitleLine1')}
            <br />
            <span className="relative inline-block">
              <span className="relative z-10">{t('heroTitleLine2')}</span>
              <span aria-hidden className="absolute inset-x-0 bottom-1 -z-0 h-3 bg-brand-teal" />
            </span>
          </p>
          <p className="mt-4 max-w-md text-base leading-relaxed text-brand-cream/80">
            {t('heroSubtitle')}
          </p>
        </div>
      </div>

      {/* ── Mobile header ── */}
      <div className="flex items-center justify-between gap-2.5 border-b-2 border-brand-indigo bg-brand-indigo p-4 pt-safe lg:hidden">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo className="h-8 w-8 text-brand-cream" />
          <span className="font-display text-base font-bold tracking-tight text-brand-cream">
            AgentCAD
          </span>
        </Link>
        <LanguageSwitcher />
      </div>

      {/* ── Right panel — form ── */}
      <div className="flex w-full flex-1 items-center justify-center bg-k-canvas px-4 py-12 sm:px-6 lg:w-[45%]">
        <div className="w-full max-w-[420px] rounded-2xl border-2 border-brand-indigo/15 bg-k-card p-8 sm:p-10">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-brand-indigo">
            {isSignup ? t('signupTitle') : t('loginTitle')}
          </h1>
          <p className="mt-2 text-sm text-k-ink/80">
            {isSignup ? t('signupSubtitle') : t('loginSubtitle')}
          </p>

          {/* Error */}
          <div aria-live="polite" role={error ? 'alert' : undefined} className="mt-6 min-h-[24px]">
            {error && (
              <div className="rounded-lg border-2 border-brand-indigo bg-brand-cream px-3 py-2 text-sm font-semibold text-brand-indigo">
                {error}
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-brand-indigo"
              >
                {t('emailLabel')}
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('emailPlaceholder')}
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-brand-indigo"
              >
                {t('passwordLabel')}
              </label>
              <Input
                id="password"
                type="password"
                autoComplete={isSignup ? 'new-password' : 'current-password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('passwordPlaceholder')}
              />
            </div>

            <Button type="submit" disabled={isSubmitting} className="w-full" size="xl">
              {isSubmitting
                ? t('submitting')
                : isSignup
                  ? t('submitSignup')
                  : t('submitLogin')}
            </Button>
          </form>

          {/* Toggle */}
          <p className="mt-8 text-center text-sm text-k-ink/80">
            {isSignup ? t('hasAccount') : t('noAccount')}
            <button
              type="button"
              onClick={() => {
                setIsSignup(!isSignup);
                setError('');
              }}
              className="ml-1 font-bold text-brand-indigo underline-offset-2 hover:underline"
            >
              {isSignup ? t('toggleToLogin') : t('toggleToSignup')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
